import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import {
    areaDescriptionInputSchema,
    type AreaDescriptionDocType,
    type ParcelDocType
} from '@ast-data/shared';
import { useStableQuery } from '../../db/useStableQuery';
import {
    dateColumn,
    type GridColDef,
    naturalSortComparator
} from '../../lib/dataGrid';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import { usePersonUserId } from '../users/personUserId';
import { parcelPickerLabel } from './parcelPickerLabel';
import { compareNatural } from '../../utils/compare';

/**
 * The Flächenbeschreibung fields editable in the grid: who wrote it up and the
 * two measurements. Fläche and Planum are the record's key and stay with the
 * form, which checks them for duplicates; the three texts are long prose.
 */
export function useInlineAreaDescriptionFields(): InlineField[] {
    const { resolve } = usePersonUserId();

    return useMemo(
        () => [
            { field: 'length' },
            { field: 'width' },
            {
                field: 'editor',
                editor: { kind: 'person' },
                derive: (value: unknown) => ({
                    editorId: resolve(String(value ?? ''))
                })
            }
        ],
        [resolve]
    );
}

/** The three texts — in the column picker and the filter panel, hidden by default. */
const AREA_DESCRIPTION_DETAIL_FIELDS = [
    'conditionAtRecording',
    'naturalSoil',
    'findingsOverview'
] as const;

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_AREA_DESCRIPTION_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(
        AREA_DESCRIPTION_DETAIL_FIELDS.map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS
};

/**
 * Every Flurstück on this device, id → label. All of them rather than the
 * project's: the Auswertung shows many projects in one grid, and a Maßnahme has
 * a handful of Flurstücke.
 */
function useParcelLabels(): Map<string, string> {
    const parcelsQuery = useStableQuery<ParcelDocType>({ selector: {} });
    const { results: parcelDocs } = useLiveRxQuery<ParcelDocType>({
        collection: 'parcels',
        query: parcelsQuery
    });
    return useMemo(
        () =>
            new Map(
                parcelDocs.map((doc) => {
                    const parcel = doc.toMutableJSON() as ParcelDocType;
                    return [parcel.id, parcelPickerLabel(parcel)];
                })
            ),
        [parcelDocs]
    );
}

export function useAreaDescriptionsColumns({
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineAreaDescriptionFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: AreaDescriptionDocType) => void;
    onDelete: (row: AreaDescriptionDocType) => void;
    onHistory: (row: AreaDescriptionDocType) => void;
}): GridColDef<AreaDescriptionDocType>[] {
    const intl = useIntl();
    const actionsColumn = useRecordActionsColumn<AreaDescriptionDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<AreaDescriptionDocType>();
    const inlineEdit = useInlineEditColumn<AreaDescriptionDocType>(
        areaDescriptionInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<AreaDescriptionDocType>(
        (row) => row.recordedAt
    );
    const parcelLabels = useParcelLabels();
    const header = (field: string) =>
        intl.formatMessage({ id: `areaDescription.field.${field}` });
    const metres = intl.formatMessage({ id: 'common.metres' });

    const measureColumn = (
        field: 'length' | 'width'
    ): GridColDef<AreaDescriptionDocType> => ({
        field,
        headerName: `${header(field)} (${metres})`,
        width: 110,
        align: 'right',
        headerAlign: 'right',
        sortComparator: naturalSortComparator,
        ...(inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {})
    });

    return [
        {
            field: 'area',
            headerName: header('area'),
            width: 110,
            sortComparator: naturalSortComparator,
            // Bold like every grid's key column — Fläche × Planum × Sondage is the key.
            cellClassName: 'grid-cell-bold'
        },
        {
            field: 'planum',
            headerName: header('planum'),
            width: 100,
            sortComparator: naturalSortComparator,
            cellClassName: 'grid-cell-bold'
        },
        {
            field: 'sondage',
            headerName: header('sondage'),
            width: 100,
            sortComparator: naturalSortComparator,
            cellClassName: 'grid-cell-bold'
        },
        isoWeekColumn,
        dateColumn<AreaDescriptionDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'common.recordedAt' }),
            width: 150
        }),
        {
            field: 'parcelIds',
            headerName: header('parcels'),
            flex: 1,
            minWidth: 200,
            valueGetter: (_value, row) =>
                row.parcelIds
                    .map((id) => parcelLabels.get(id))
                    .filter((label): label is string => Boolean(label))
                    .sort(compareNatural)
                    .join(', ')
        },
        measureColumn('length'),
        measureColumn('width'),
        {
            field: 'editor',
            headerName: header('editor'),
            width: 130,
            ...(inlineOf('editor')
                ? inlineEdit('editor', inlineOf('editor'))
                : {})
        },
        ...AREA_DESCRIPTION_DETAIL_FIELDS.map<
            GridColDef<AreaDescriptionDocType>
        >((field) => ({
            field,
            headerName: header(field),
            width: 200
        })),
        ...auditColumns,
        actionsColumn
    ];
}
